/**
 * The one auto-growing-textarea rule in the app.
 *
 * Both places a person writes to an agent — the workspace composer and the
 * assistant panel — grow the same way: a floor and a ceiling measured in lines,
 * derived from the element's *actual* line-height so the cap is exactly N lines
 * rather than a magic pixel number that drifts every time the type scale moves.
 * Only the two line counts differ (the composer drafts briefs, the assistant
 * takes a sentence), which is why they are parameters and not two functions.
 */

/** Optical slack under the floor so the caret is not wedged against the
 * footer. Part of the floor, not of the per-line growth. */
const FLOOR_SLACK = 2;
/** Only used if the element is not laid out yet (`line-height: normal`). */
const FALLBACK_LINE_HEIGHT = 19;

/** Resizes `element` between `minLines` and `maxLines`, and reports whether the
 * content now overflows that ceiling (i.e. the box is scrolling). */
export function resizeTextarea(
  element: HTMLTextAreaElement | null,
  { minLines, maxLines }: { minLines: number; maxLines: number },
): boolean {
  if (!element) return false;
  const styles = window.getComputedStyle(element);
  const parsedLineHeight = Number.parseFloat(styles.lineHeight);
  const lineHeight = Number.isFinite(parsedLineHeight) && parsedLineHeight > 0
    ? parsedLineHeight
    : FALLBACK_LINE_HEIGHT;
  const insets = (Number.parseFloat(styles.paddingTop) || 0) + (Number.parseFloat(styles.paddingBottom) || 0);
  const min = Math.round(lineHeight * minLines + insets + FLOOR_SLACK);
  const max = Math.round(lineHeight * maxLines + insets);
  element.style.height = "auto";
  const content = element.scrollHeight;
  element.style.height = `${Math.min(Math.max(content, min), max)}px`;
  const overflowing = content > max;
  element.style.overflowY = overflowing ? "auto" : "hidden";
  return overflowing;
}
